import { useState } from 'react';
import { Login } from '../presentational/Login';
import { useForm } from 'lib/utils';
import { handleSignIn, initAmplify, UserSignInData } from 'lib/utils/amplify';
import { useNavigate } from 'react-router-dom';
import { CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE } from 'lib/constants/amplify';
import { AUTH_EMAIL } from '../../../../constants/shared/routes';

import {
    REACT_APP_AMPLIFY_POOL_ID,
    REACT_APP_AMPLIFY_POOL_CLIENT_ID,
} from 'config';
import { setAuthStep } from 'utils/generic';
import { AuthStep } from 'constants/shared/enums';
import { ErrorName, CustomerIOEvent } from 'lib/type/enums';
import { trackCustomerIOEvent } from 'utils/customerIO';

initAmplify(REACT_APP_AMPLIFY_POOL_ID, REACT_APP_AMPLIFY_POOL_CLIENT_ID);

export const LoginContainer = () => {
    const navigate = useNavigate();
    const [isPosting, setIsPosting] = useState(false);
    const [error, setError] = useState<string | null>('');
    const [form, handleChange] = useForm({
        username: '',
    });

    const prepareUserSignInData = async (form: {
        username: string;
    }): Promise<UserSignInData> => {
        const { username } = form;
        const user: UserSignInData = {
            username: username.trim(),
        };
        return user;
    };

    const getLocation = () => {
        if (!navigator.geolocation) {
            console.warn('Geolocation is not supported by this browser.');
            return;
        }

        navigator.permissions.query({ name: 'geolocation' }).then(results => {
            if (results.state === 'granted' || results.state === 'denied')
                return;
            navigator.geolocation.getCurrentPosition(
                position => {
                    trackCustomerIOEvent(CustomerIOEvent.GPS_LOCATION, {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                },
                error => {
                    console.error('Error getting location:', error);
                },
            );
        });
    };

    const handleSubmit = async () => {
        setIsPosting(true);
        const user = await prepareUserSignInData(form);
        getLocation();
        try {
            const signInResult = await handleSignIn(user);
            const { signInStep, additionalInfo } = signInResult.nextStep;
            trackCustomerIOEvent(CustomerIOEvent.LOGIN, {
                email: user.username,
            });
            if (signInStep === CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE) {
                localStorage.setItem(
                    'userInfo',
                    JSON.stringify({ ...additionalInfo, ...user }),
                );
                navigate(AUTH_EMAIL);
                setAuthStep(AuthStep.EMAIL_SIGN_IN_FLOW);
            }
        } catch (e) {
            const error = e as { message: string; name: string };
            if (error.name === ErrorName.USER_NOT_FOUND_EXCEPTION) {
                setError(null);
                return;
            }
            setError(error.message);
        } finally {
            setIsPosting(false);
        }
    };

    return (
        <Login
            {...form}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isPosting={isPosting}
            error={error}
        />
    );
};
